<script setup lang="ts">
import { computed, ref } from 'vue'

import { useAccount } from '../composables/useAccount'
import { useAccountSession } from '../composables/useAccountSession'
import { useRbac } from '../composables/useRbac'
import { useWorkspace } from '../composables/useWorkspace'
import type { AccountItem } from '../models/account.types'
import type { WorkspaceItem } from '../models/workspace.types'
import { resolveProfilePictureUrl } from '../service/resolveProfilePictureUrl'

import AccountSwitcher from './AccountSwitcher.vue'
import WorkspaceSwitcher from './WorkspaceSwitcher.vue'

import { useI18n } from '@/shared/i18n'
import { Button } from '@/shared/ui/button'
import { Icon } from '@/shared/ui/icon'
import { Popover } from '@/shared/ui/popover'
import { Skeleton } from '@/shared/ui/skeleton'

const props = withDefaults(defineProps<{ loggingOut?: boolean; logoutLabel: string }>(), {
  loggingOut: false,
})
const emit = defineEmits<{ logout: []; onboard: []; workspaceSettings: [ws: WorkspaceItem] }>()

const { t } = useI18n()
const { account, accountDetail, accountId, accounts, accountUserId, userId, permissions } =
  useAccount()
const { workspace, workspaces } = useWorkspace()
const { roles } = useRbac()
const { isBooting } = useAccountSession()
const switchingAccount = ref(false)
const switchingWorkspace = ref(false)
let closeAfterAccountSwitch: (() => void) | null = null
let closeAfterWorkspaceSwitch: (() => void) | null = null
const busy = computed(
  () => props.loggingOut || isBooting.value || switchingAccount.value || switchingWorkspace.value,
)
const canCreateWorkspace = computed(() => {
  const roleAllowed =
    accountUserId.value === userId.value ||
    roles.value.some((role) => ['admin', 'editor'].includes(role))
  const limit = Number(permissions.value.workspaces_)
  return roleAllowed && Number.isFinite(limit) && limit > workspaces.value.length
})
const displayAccount = computed(() => (isBooting.value ? null : account.value))
const displayWorkspace = computed(() => {
  if (isBooting.value || !workspace.value) return null
  if (accountId.value === undefined || workspace.value.account_id === undefined) {
    return workspace.value
  }
  return String(workspace.value.account_id) === String(accountId.value) ? workspace.value : null
})
const profilePicture = computed(() => {
  if (isBooting.value) return null
  const candidate =
    accountDetail.value?.profile_picture_path ?? displayAccount.value?.profile_picture_path
  return resolveProfilePictureUrl(candidate)
})

const initials = computed(() => {
  const value = String(displayAccount.value?.name ?? t('account.area.accountFallback'))
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toLocaleUpperCase()
})

function selectAccount(_account: AccountItem, close: () => void): void {
  closeAfterAccountSwitch = close
}

function selectWorkspace(_workspace: WorkspaceItem, close: () => void): void {
  closeAfterWorkspaceSwitch = close
}

function syncAccountSwitching(value: boolean): void {
  switchingAccount.value = value
  if (!value && closeAfterAccountSwitch) {
    closeAfterAccountSwitch()
    closeAfterAccountSwitch = null
  }
}

function syncWorkspaceSwitching(value: boolean): void {
  switchingWorkspace.value = value
  if (!value && closeAfterWorkspaceSwitch) {
    closeAfterWorkspaceSwitch()
    closeAfterWorkspaceSwitch = null
  }
}

function startOnboarding(close: () => void): void {
  close()
  emit('onboard')
}

function openWorkspaceSettings(workspace: WorkspaceItem, close: () => void): void {
  close()
  emit('workspaceSettings', workspace)
}

function requestLogout(): void {
  if (!busy.value) emit('logout')
}
</script>

<template>
  <Popover
    trigger-variant="plain"
    panel-variant="plain"
    placement="bottom-end"
    :offset="20"
    :disabled="busy"
    :trigger-label="t('account.area.openMenu')"
    :panel-label="t('account.area.panel')"
  >
    <template #trigger="{ open }">
      <span
        class="account-area__trigger flex items-center justify-end gap-sm rounded-sm text-primary cursor-pointer"
        :class="{ 'account-area__trigger--open': open }"
      >
        <span
          v-if="isBooting"
          class="account-area__trigger-loading flex items-center gap-sm"
          data-testid="account-area-loading"
        >
          <Skeleton class="account-area__trigger-copy-skeleton" />
          <Skeleton class="account-area__avatar-skeleton" />
        </span>
        <template v-else>
          <span
            class="account-area__trigger-copy flex max-w-40 flex-col items-end text-sm leading-[1.15] max-[56rem]:hidden"
          >
            <strong>{{ displayAccount?.name ?? t('account.area.accountFallback') }}</strong>
            <span v-if="displayWorkspace">{{ displayWorkspace.label }}</span>
          </span>
          <img
            v-if="profilePicture"
            class="account-area__avatar account-area__avatar--image grid flex-none place-items-center overflow-hidden rounded-full w-10 h-10 text-[0.75rem] border border-border bg-surface object-contain shadow-card"
            :src="profilePicture"
            alt=""
            data-testid="account-area-avatar-image"
          />
          <span
            v-else
            class="account-area__avatar account-area__avatar--initials grid flex-none place-items-center overflow-hidden rounded-full w-10 h-10 text-[0.75rem] bg-primary text-primary-fg"
            data-testid="account-area-avatar-initials"
            aria-hidden="true"
            >{{ initials }}</span
          >
          <Icon
            name="chevron-down"
            :size="20"
            class="account-area__trigger-chevron stroke-3 transition-[transform] duration-150 ease-[ease] max-[56rem]:hidden"
          />
        </template>
      </span>
    </template>

    <template #default="{ close }">
      <section
        class="account-area__panel w-[min(24rem,_calc(100vw_-_2rem))] p-5 rounded-md bg-surface shadow-pop"
      >
        <div class="account-area__identity flex items-center min-h-16 gap-sm">
          <img
            v-if="profilePicture"
            class="account-area__hero-avatar account-area__avatar--image grid flex-none place-items-center overflow-hidden rounded-full w-16 h-16 font-bold border border-border bg-surface object-contain shadow-card"
            :src="profilePicture"
            alt=""
          />
          <span
            v-else
            class="account-area__hero-avatar account-area__avatar--initials grid flex-none place-items-center overflow-hidden rounded-full w-16 h-16 font-bold bg-primary text-primary-fg"
          >
            {{ initials }}
          </span>

          <AccountSwitcher
            v-if="accounts.length > 1 || isBooting"
            compact
            :disabled="busy"
            @select="selectAccount($event, close)"
            @switching="syncAccountSwitching"
          />
          <strong
            v-else
            class="account-area__identity-name overflow-hidden text-primary text-[1rem] text-ellipsis whitespace-nowrap"
          >
            {{ account?.name ?? t('account.area.accountFallback') }}
          </strong>
        </div>

        <div
          class="account-area__workspace-heading flex items-center mt-10 min-h-11.25 pb-sm border-b border-b-border text-[0.875rem]"
        >
          <strong>{{ t('account.area.workspaces') }}</strong>
          <button
            v-if="canCreateWorkspace"
            type="button"
            :disabled="busy"
            @click="startOnboarding(close)"
          >
            <Icon name="plus" :size="10" aria-hidden="true" />
            {{ t('account.area.newWorkspace') }}
          </button>
        </div>
        <div
          class="account-area__workspace-list min-h-20 max-h-[min(15rem,_var(--ui-floating-available-height))] overflow-y-auto py-[var(--ui-space-sm)_var(--ui-space-md)]"
        >
          <WorkspaceSwitcher
            variant="menu"
            :disabled="busy"
            @select="selectWorkspace($event, close)"
            @onboard="startOnboarding(close)"
            @settings="openWorkspaceSettings($event, close)"
            @switching="syncWorkspaceSwitching"
          />
        </div>

        <Button
          block
          :disabled="busy"
          :loading="loggingOut"
          data-testid="account-logout"
          @click="requestLogout"
        >
          {{ logoutLabel }}
          <template #end><Icon name="arrow-right" :size="16" /></template>
        </Button>
      </section>
    </template>
  </Popover>
</template>

<style scoped>
.account-area__trigger-copy > * {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.account-area__trigger-copy span {
  font-weight: 300;
}
.account-area__trigger--open .account-area__trigger-chevron {
  transform: rotate(180deg);
}
.account-area__identity > :last-child {
  min-width: 0;
  flex: 1;
}
.account-area__workspace-heading strong {
  color: var(--ui-color-fg);
}
.account-area__workspace-heading button {
  display: inline-flex;
  align-items: center;
  gap: var(--ui-space-2xs);
  margin-inline-start: auto;
  padding-block: var(--ui-space-2xs);
  padding-inline: 0;
  border-style: solid;
  border-width: 0px;
  background-color: transparent;
  color: var(--ui-color-info);
  font: inherit;
  font-size: 0.75rem;
  cursor: pointer;
}
.account-area__workspace-heading button:disabled {
  opacity: 50%;
  cursor: not-allowed;
}
</style>
